import { Location, SubscriptionPlan } from '@m/types/api';
import { apiFetch, apiFetchV2 } from '@m/utils/http';
import { useMutation, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';

export interface CustomerSubscriptionJoinCodeResponse {
  siteDetails: Location | null;
  subscriptionPlan: SubscriptionPlan | null;
}

export interface ValidFromView {
  description: string;
  label: string;
}

export interface SubscriptionDetailsView {
  accessEnd?: string;
  accessStart: string;
  addedAt: string;
  baseRateMonthly: number;
  billingStartAt?: string;
  effectiveFrom: string;
  enterpriseSubscriptionPlanId?: number;
  fees: any[];
  label?: any;
  maxVehicles?: number;
  minVehicles?: number;
  nextChargeAmount?: number;
  nextChargeDate?: string;
  paymentCollectorSiteId?: number;
  planType: string;
  siteFees: any[];
  siteIds: number[];
  subscriptionPlanId?: number;
  tax?: any;
  totalRateMonthly: number;
  totalTaxMonthly?: number;
  validFrom?: ValidFromView[];
}

export interface SubscriptionStatusView {
  isCancelled: boolean;
}

export interface SubscriptionView {
  details: SubscriptionDetailsView;
  id: number;
  membershipId?: string;
  status: SubscriptionStatusView;
}

export interface SiteRowView {
  city: string;
  id: number;
  latitude: number;
  longitude: number;
  name: string;
  state: string;
  street: string;
  timezone?: string;
  uuid: string;
  zip: string;
}

export interface ActiveEnterpriseSubscriptionDetailsView {
  enterpriseName?: string;
  subscribedAt?: string;
}

export interface SiteSubscriptionView {
  enterpriseDetails?: ActiveEnterpriseSubscriptionDetailsView;
  site: SiteRowView;
  subscription: SubscriptionView;
}

const getCustomerSubscriptionJoinCode = async (planId: string) => {
  const response = await apiFetch<CustomerSubscriptionJoinCodeResponse>(
    `/self-service/subscription/plan-for-user/${planId}`,
  );
  if (!response.success) {
    throw new Error(response?.statusText || 'Failed to fetch customer subscription join code');
  }
  return response.data;
};

export const useCustomerSubscriptionJoinCode = (planId: string) =>
  useQuery({
    queryKey: ['customer-subscription-join-code', planId],
    queryFn: () => getCustomerSubscriptionJoinCode(planId),
    enabled: !!planId,
  });

const getCustomerSubscription = async () => {
  const response = await apiFetchV2<SiteSubscriptionView[]>('/customer/subscription');
  return response;
};

export const useCustomerSubscription = (
  options?: Omit<UseQueryOptions<SiteSubscriptionView[], Error>, 'queryKey' | 'queryFn'>,
) =>
  useQuery<SiteSubscriptionView[], Error>({
    queryKey: ['customer-subscription'],
    queryFn: getCustomerSubscription,
    ...options,
  });

const getSiteSubscriptions = async (siteId: number) => {
  const response = await apiFetchV2<SubscriptionPlan[]>(`/subscription/plan/site/${siteId}`);
  return response;
};

export const useSiteSubscriptions = (siteId?: number) =>
  useQuery({
    queryKey: ['site-subscriptions', siteId],
    queryFn: () => getSiteSubscriptions(siteId!),
    enabled: !!siteId,
  });

const confirmMembershipBilling = async (membershipId: string) => {
  const response = await apiFetchV2(
    `/self-service/subscription/membership/${membershipId}/billing/confirm`,
    {
      method: 'POST',
    },
  );
  return response;
};

export const useConfirmMembershipBilling = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (membershipId: string) => confirmMembershipBilling(membershipId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-subscription'] });
    },
  });
};

export type CancelSubscriptionParams = {
  subscriptionCancellationReasonId?: number;
  subscriptionId: number;
};

const cancelCustomerSubscription = async (params: CancelSubscriptionParams) => {
  const { subscriptionId, subscriptionCancellationReasonId } = params;
  await apiFetchV2(`/customer/subscription/${subscriptionId}`, {
    method: 'DELETE',
    body: { subscriptionCancellationReasonId },
  });
};

export const useCancelSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: CancelSubscriptionParams) => cancelCustomerSubscription(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-subscription'] });
    },
  });
};

export interface ProrationDetails {
  subscription: {
    details: {
      accessEnd?: string;
      accessStart: string;
      addedAt: number;
      baseRateMonthly: number;
      billingStartAt?: string;
      effectiveFrom: string;
      fees: {
        amount: number;
        fee?: {
          name?: string;
        };
      }[];
      label?: string | null;
      nextChargeAmount: number;
      nextChargeDate?: string;
      planType: string;
      siteFees: {
        amount: number;
        fee?: {
          name?: string;
        };
      }[];
      siteIds: number[];
      tax?: {
        taxAmount: number;
        taxRate: number;
      };
      totalRateMonthly: number;
      totalTaxMonthly: number;
    };
  };
}

const getSubscriptionCheckout = async (planId: number, startDate: string) => {
  const response = await apiFetchV2<ProrationDetails>(
    `/customer/subscription-plan/checkout/${planId}`,
    {
      method: 'POST',
      params: { startDate },
    },
  );
  return response;
};

export const useSubscriptionCheckout = (planId?: number, startDate?: string) =>
  useQuery({
    queryKey: ['subscription-checkout', planId, startDate],
    queryFn: () => getSubscriptionCheckout(planId!, startDate!),
    enabled: !!planId && !!startDate,
  });
